import React, { useEffect, useState } from "react";
import ChartComponent from "../webappStats/ChartComponent";
import HelperMetiers360 from "../../services/HelpersMetiers360";
import axios from '../../services/axios';
import Loader from "../widgets/Loader";
import { useStoreActions, useStoreState } from "easy-peasy";
import { Button, Card, Col, Row, ToggleButton, ToggleButtonGroup } from "react-bootstrap";
import useCustomGetStoreState from "../../hooks/useCustomGetStoreState";
import SelectMultipleClientsComponent from "../widgets/selectClient/SelectMultipleClientsComponent";
import SelectMultipleItemsComponent from "../widgets/selectMultipleItemsComponent/SelectMultipleItemsComponent";

const HeadsetsStatComponent = () => {
    const { apiData: { ACTIONS } } = useStoreState(state => state.actionSlugs);
    const { allClients } = useCustomGetStoreState('clients');
    const { fetchContactsM360 } = useStoreActions(actions => actions.clients);
    const { contactsM360, allClientsCRM } = useStoreState(state => state.clients);

    const [isFetching, setIsFetching] = useState(false);
    const [graphsData, setGraphsData] = useState({});
    const [isAvailableData, setIsAvailableData] = useState({});
    const [lastPingsToDisplay, setLastPingsToDisplay] = useState("all");
    const [onlyActiveSubscriptionHeadsets, setOnlyActiveSubscriptionHeadsets] = useState(false);
    const [selectedClientUniqueIds, setSelectedClientUniqueIds] = useState([]);
    const [isExpand, setIsExpand] = useState(false);
    const [contactsSelected, setContactsSelected] = useState([]);

    const fetchDataForGraph = async () => {
        if (!ACTIONS?.headsetsStats || isFetching) return;

        setIsFetching(true);
        const entries = Object.entries(ACTIONS.headsetsStats);

        try {
            let clientsCRMUniqueIdsWithContactSelected = []
            if (contactsSelected.length) {
                const contactsSelectedIds = contactsSelected.map(c => c.id);
                const clientsCRMWithContactSelected = allClientsCRM?.filter(client => client.contactsM360?.some(contact => contactsSelectedIds.some(id => contact.id == id)))
                clientsCRMUniqueIdsWithContactSelected = clientsCRMWithContactSelected?.map(client => client.uniqueId)
            }

            const results = await Promise.allSettled(
                entries.map(async ([key, stat]) => {
                    if (!stat.url) return [key, null];
                    const response = await axios.post(
                        stat.url, 
                        {
                            onlyActiveSubscriptionHeadsets,
                            clientUniqueIds: [...selectedClientUniqueIds, ...clientsCRMUniqueIdsWithContactSelected]
                        },
                        { responseType: "json" }
                    )
                    return [key, response];
                })
            );

            const dataObject = Object.fromEntries(results.filter(r => r.status === "fulfilled").map(r => r.value));
            const graphDataObject = Object.fromEntries(Object.entries(dataObject).map(([name, value]) => [name, value?.length ? value : []]));
            const isAvailableObject = Object.fromEntries(Object.entries(dataObject).map(([name, value]) => [name, !!value?.length]));

            setGraphsData(graphDataObject);
            setIsAvailableData(isAvailableObject);
        } catch (error) {
            Promise.reject(new Error(error));
        } finally {
            setIsFetching(false);
        }
    };

    useEffect(() => {
        fetchDataForGraph();
        !contactsM360.length && fetchContactsM360()
    }, []);

    const keepOnlyAppVersion = (app_version) => {
        return app_version.includes("Classe")
            ? app_version.split(" ")[1]
            : app_version
    }

    const getNearestGreater2n = (val) => typeof val === 'number'
        ? 2 ** (Math.ceil(Math.log(val) / Math.log(2)))
        : 0

    const getHeadsetsTotalStorageByLevel = (headsetTotalStorageCount) => {
        const headsetTotalStorageLevels = headsetTotalStorageCount?.map(h => {
            return {
                storage_total_level: getNearestGreater2n(h.storage_total_gb),
                count: h.count
            }
        })
        var headsetGroupByTotalStorageLevel = [];
        headsetTotalStorageLevels.reduce(function (res, value) {
            if (!res[value.storage_total_level]) {
                res[value.storage_total_level] = { storage_total_level: value.storage_total_level, count: 0 };
                headsetGroupByTotalStorageLevel.push(res[value.storage_total_level])
            }
            res[value.storage_total_level].count += value.count;
            return res;
        }, {});

        return headsetGroupByTotalStorageLevel;
    }

    const tableCsv = {
        appVersions: graphsData?.appVersions?.sort((a, b) => HelperMetiers360.localeCompareWithNullable(a.class_app_version, b.class_app_version) !== 0
            ? HelperMetiers360.sortHeadsetAppVersions(keepOnlyAppVersion(a.class_app_version), keepOnlyAppVersion(b.class_app_version))
            : HelperMetiers360.sortHeadsetAppVersions(a.app_version, b.app_version)),
        lastPings: graphsData?.lastPings,
        unupdatedLastPings: graphsData?.unupdatedLastPings,
        headsetTypes: graphsData?.headsetTypes?.sort((a, b) => HelperMetiers360.localeCompareWithNullable(a.count, b.count)),
        headsetTotalStorage: graphsData?.headsetTotalStorage?.sort((a, b) => HelperMetiers360.localeCompareWithNullable(a.count, b.count)),
        usedStorageMemory: graphsData?.usedStorageMemory,
    };

    const csvSort = {
        appVersions: [
            {
                value: 'class_app_version',
                label: 'Classe de version d\'application',
                test: HelperMetiers360.isArrayContainsValue(graphsData?.appVersions, "class_app_version"),
                display: (data) => data.class_app_version
            },
            {
                value: 'app_version',
                label: 'Version d\'application',
                test: HelperMetiers360.isArrayContainsValue(graphsData?.appVersions, "app_version"),
                display: (data) => data.app_version
            },
            {
                value: 'count',
                label: 'Nombre de casques',
                test: HelperMetiers360.isArrayContainsValue(graphsData?.appVersions, "count"),
                display: (data) => data.count
            }
        ],
        lastPings: [
            {
                value: 'last_ping',
                label: 'Dernière mise à jour',
                test: HelperMetiers360.isArrayContainsValue(graphsData?.lastPings, "last_ping"),
                display: (data) => data.last_ping
            },
            {
                value: 'count',
                label: 'Nombre de casques',
                test: HelperMetiers360.isArrayContainsValue(graphsData?.lastPings, "count"),
                display: (data) => data.count
            }
        ],
        unupdatedLastPings: [
            {
                value: 'last_ping',
                label: 'Dernière mise à jour',
                test: HelperMetiers360.isArrayContainsValue(graphsData?.unupdatedLastPings, "last_ping"),
                display: (data) => data.last_ping
            },
            {
                value: 'count',
                label: 'Nombre de casques',
                test: HelperMetiers360.isArrayContainsValue(graphsData?.unupdatedLastPings, "count"),
                display: (data) => data.count
            }
        ],
        headsetTypes: [
            {
                value: 'label',
                label: 'Type de casque',
                test: HelperMetiers360.isArrayContainsValue(graphsData?.headsetTypes, "label"),
                display: (data) => data.label
            },
            {
                value: 'count',
                label: 'Nombre de casques',
                test: HelperMetiers360.isArrayContainsValue(graphsData?.headsetTypes, "count"),
                display: (data) => data.count
            }
        ],
        headsetTotalStorage: [
            {
                value: 'storage_total_gb',
                label: 'Stockage total (Gb)',
                test: HelperMetiers360.isArrayContainsValue(graphsData?.headsetTotalStorage, "storage_total_gb"),
                display: (data) => data.storage_total_gb
            },
            {
                value: 'count',
                label: 'Nombre de casques',
                test: HelperMetiers360.isArrayContainsValue(graphsData?.headsetTotalStorage, "count"),
                display: (data) => data.count
            }
        ],
        usedStorageMemory: [
            {
                value: 'used_memory_class',
                label: 'Stockage utilisé (%)',
                test: HelperMetiers360.isArrayContainsValue(graphsData?.usedStorageMemory, "used_memory_class"),
                display: (data) => data.used_memory_class
            },
            {
                value: 'count',
                label: 'Nombre de casques',
                test: HelperMetiers360.isArrayContainsValue(graphsData?.usedStorageMemory, "count"),
                display: (data) => data.count
            },
            {
                value: 'percent',
                label: 'Nombre de casques (%)',
                test: HelperMetiers360.isArrayContainsValue(graphsData?.usedStorageMemory, "count"),
                display: (data) => Math.round(data.count * 100 / graphsData?.usedStorageMemory?.reduce((prev, current) => prev + current.count, 0))
            }
        ]
    };

    const clientsFilter = allClients?.length > 1 && <>
        <Row className="stats-select-clients">
            <Card className="contents_details_card">
                <Card.Title className="contents_details_title d-flex justify-content-between mx-4 cursor-pointer"
                    onClick={() => setIsExpand(prev => !prev)}>
                    {selectedClientUniqueIds?.length +
                        (selectedClientUniqueIds?.length > 1
                            ? " établissements sélectionnés"
                            : " établissement sélectionné")
                        + " pour analyse"}
                </Card.Title>
                <Card.Body className={"contents_details_body" + (isExpand ? "" : " visually-hidden")}>
                    <SelectMultipleClientsComponent
                        disabled={isFetching}
                        nameSelect={`stats[clients][]`}
                        selectedClients={selectedClientUniqueIds}
                        setSelectedClients={setSelectedClientUniqueIds} />
                </Card.Body>
            </Card>
        </Row>
        <div className="expand-btn cursor-pointer" onClick={() => setIsExpand(prev => !prev)}>
            {isExpand ? <i className="fa fa-angle-up fa-lg" /> : <i className="fa fa-angle-down fa-lg" />}
        </div>
    </>;

    const activeSubscriptionFilter = <div className="d-flex align-items-center">
        <label className="label-select">Casques&nbsp;: </label>
        <div className='choices-selector-inline'>
            <div className={!onlyActiveSubscriptionHeadsets ? 'selected' : null}
                onClick={() => setOnlyActiveSubscriptionHeadsets(false)}
                onKeyDown={() => setOnlyActiveSubscriptionHeadsets(false)}>
                Tous</div>
            <div className={onlyActiveSubscriptionHeadsets ? 'selected' : null}
                onClick={() => setOnlyActiveSubscriptionHeadsets(true)}
                onKeyDown={() => setOnlyActiveSubscriptionHeadsets(true)}>
                Avec abonnement actif</div>
        </div>
    </div>;

    const contactM360Filter = <div className="d-flex align-items-center">
        <label className="label-select">Contacts&nbsp;M360&nbsp;: </label>
        <span className="d-flex flex-column w-100">
            <SelectMultipleItemsComponent itemsSelected={contactsSelected} setItemsSelected={setContactsSelected}
                items={contactsM360} name="contacts" itemKey="id" itemValue="name" id='select-contacts' />
        </span>
    </div>

    return <>
        <div className="mb-5">
            <Row>
                {clientsFilter}
            </Row>
            <Row>
                <Col>{activeSubscriptionFilter}</Col>
                <Col>{contactM360Filter}</Col>
            </Row>
            <Row className="justify-content-center">
                <Button id="btnInitFilters" className="shadow w-auto" variant="success" size="sm" onClick={fetchDataForGraph}>
                    Filtrer
                </Button>
            </Row>
        </div>
        {isFetching
            ? <Loader />
            : Object.keys(graphsData).length !== 0 && <>
                <Row>
                    <Col>
                        <ChartComponent
                            title="Nombre de casque par version d'application"
                            tableCsv={tableCsv?.appVersions}
                            csvSort={csvSort?.appVersions}
                            csvFilename="nb_casques_par_version_application"
                            graphType="pie2Levels"
                            data={graphsData?.appVersions}
                            isDataAvailable={isAvailableData?.appVersions}
                            dataNamesLabel="app_version"
                            parentsDataNamesLabel="class_app_version"
                            dataValuesLabel="count"
                        />
                    </Col>
                    <Col>
                        <ChartComponent
                            title="Répartition de la mise à jour des casques"
                            tableCsv={lastPingsToDisplay === "all"
                                ? tableCsv?.lastPings
                                : tableCsv?.unupdatedLastPings}
                            csvSort={lastPingsToDisplay === "all"
                                ? csvSort?.lastPings
                                : csvSort?.unupdatedLastPings}
                            csvFilename={"repartition_maj_casques_" + (lastPingsToDisplay === "unupdated" ? "jamais_maj_" : "") + "par_mois"}
                            graphType="bar"
                            data={lastPingsToDisplay === "all"
                                ? graphsData?.lastPings
                                : graphsData?.unupdatedLastPings}
                            isDataAvailable={lastPingsToDisplay === "all"
                                ? isAvailableData?.lastPings
                                : isAvailableData?.unupdatedLastPings}
                            dataNamesLabel="last_ping"
                            dataValuesLabel="count"
                            toggleButton={<ToggleButtonGroup type="radio" name="lastPingsGraph" onChange={(type) => setLastPingsToDisplay(type)}
                                value={lastPingsToDisplay} className='ms-2'>
                                <ToggleButton value="all" key="all" id="all">Tous</ToggleButton>
                                <ToggleButton value="unupdated" key="unupdated" id="unupdated">Casque jamais MAJ *</ToggleButton>
                            </ToggleButtonGroup>}
                        />
                        <i>* : Ce sont les casques tels que le dernier ping est dans la semaine de la date de création.</i>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <ChartComponent
                            title="Type de casques"
                            tableCsv={tableCsv?.headsetTypes}
                            csvSort={csvSort?.headsetTypes}
                            csvFilename="type_casques"
                            graphType="pie"
                            data={graphsData?.headsetTypes}
                            isDataAvailable={isAvailableData?.headsetTypes}
                            dataNamesLabel="label"
                            dataValuesLabel="count"
                        />
                    </Col>
                    <Col>
                        <ChartComponent
                            title="Stockage total *"
                            tableCsv={tableCsv?.headsetTotalStorage}
                            csvSort={csvSort?.headsetTotalStorage}
                            csvFilename="stockage_total"
                            graphType="pie"
                            data={getHeadsetsTotalStorageByLevel(graphsData?.headsetTotalStorage)}
                            isDataAvailable={isAvailableData?.headsetTotalStorage}
                            dataNamesLabel="storage_total_level"
                            dataValuesLabel="count"
                        />
                        <i>* : Les données ont été regroupées par classe de stockage connue (32, 64, 128, 256, ...). Le stockage total exact est disponible dans le csv téléchargeable.</i>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <ChartComponent
                            title="Répartition des casques par taux de remplissage de la mémoire"
                            tableCsv={tableCsv?.usedStorageMemory}
                            csvSort={csvSort?.usedStorageMemory}
                            csvFilename="casques_taux_remplissage"
                            graphType="bar"
                            data={graphsData?.usedStorageMemory}
                            isDataAvailable={isAvailableData?.usedStorageMemory}
                            dataNamesLabel="used_memory_class"
                            dataValuesLabel="count"
                        />
                    </Col>
                </Row>
            </>
        }
    </>;
}

export default HeadsetsStatComponent;